// src/app/pages/dashboard/t0-dashboard/t0-dashboard.component.ts
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { ChartModule } from 'primeng/chart';
import { KnobModule } from 'primeng/knob';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  Subject,
  combineLatest,
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
  Observable
} from 'rxjs';
import { LocationSelectionService } from '../service/location-selection.service';

import { T0StatsComponent } from './t0-stats/t0-stats.component';
import { T0SimulationPanelComponent } from './t0-simulation-panel/t0-simulation-panel.component';
import { ProjectedImpactComponent } from './projected-impact/projected-impact.component';
import { T0SceneriosComponent } from './t0-scenerios/t0-scenerios.component';

import { AnalysisStore, AnalysisRun } from '../../core/shared/t0-model/analysis.store';
import { YearStore } from '../../core/shared/t0-model/year.store';
import { T0BaselineService } from '../../core/shared/t0-model/t0-baseline.service';
import { SkeletonModule } from 'primeng/skeleton';

type Horizon = 5 | 10 | 15 | 20;
type PeriodLabel = '5 years' | '10 years' | '15 years' | '20 years';

@Component({
  selector: 'app-t0-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    TooltipModule,
    ChartModule,
    KnobModule,
    SkeletonModule,
    T0StatsComponent,
    T0SimulationPanelComponent,
    ProjectedImpactComponent,
    T0SceneriosComponent
  ],
  templateUrl: './t0-dashboard.component.html'
})
export class T0DashboardComponent implements OnInit, OnDestroy {
  private readonly store = inject(AnalysisStore);
  private readonly yearStore = inject(YearStore);
  private readonly t0BaselineService = inject(T0BaselineService);
  private readonly locationSelectionService = inject(LocationSelectionService);
  private readonly destroy$ = new Subject<void>();

  // Created in injection context (field initializer) to satisfy NG0203.
  private readonly selectedCity$: Observable<string> = toObservable(
    this.locationSelectionService.selected
  ).pipe(map((selection) => selection?.city?.trim() ?? ''));

  private loadingWatchdog?: ReturnType<typeof setTimeout>;

  runs: AnalysisRun[] = [];
  loadingBaseline = true;

  selectedInfoSimulation = 'Basic Simulation';
  selectedInfoPeriod: PeriodLabel = '5 years';
  selectedCascadeSimulation = this.selectedInfoSimulation;

  horizon: Horizon = 20;

  ngOnInit(): void {
    this.horizon = this.periodToHorizon(this.selectedInfoPeriod);
    this.yearStore.setHorizon(this.horizon);

    this.loadingBaseline = true;
    this.store.clearAllRuns();

    this.bindRuns();
    this.bindBaselineReload();
    this.reloadCurrentCityBaseline();
    this.startLoadingWatchdog();
  }

  ngOnDestroy(): void {
    this.clearLoadingWatchdog();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private bindBaselineReload(): void {
    this.selectedCity$
      .pipe(
        filter((city): city is string => !!city),
        filter((city) => city.toLowerCase() !== 'sao paulo'),
        distinctUntilChanged(),
        tap(() => {
          this.loadingBaseline = true;
          this.store.clearAllRuns();
          this.startLoadingWatchdog();
        }),
        switchMap((city) => this.loadBaseline$(city)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /** Loads baseline for whatever city is currently selected, bypassing distinct checks. */
  private reloadCurrentCityBaseline(): void {
    const city = this.locationSelectionService.getSelected()?.city?.trim() ?? '';

    if (!city || city.toLowerCase() === 'sao paulo') {
      // No valid T0 city yet — the distinct stream will handle it once the
      // dropdown commits a selection. Watchdog protects against a stall.
      return;
    }

    this.loadBaseline$(city).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private loadBaseline$(city: string): Observable<void> {
    return from(this.t0BaselineService.resetAndReloadBaselineForCity(city)).pipe(
      catchError((error) => {
        console.error('[T0] Baseline load failed.', error);
        return of(void 0);
      }),
      finalize(() => this.stopLoading())
    );
  }

  private bindRuns(): void {
    combineLatest([this.store.runsList$, this.store.selectedRunId$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([runs, selId]) => {
        this.runs = runs ?? [];

        if (this.runs.length > 0) {
          this.stopLoading();
        }

        if (this.runs.length === 0) {
          return;
        }

        const selectedRunById = selId ? this.runs.find((r) => r.id === selId) : null;
        if (selectedRunById) {
          this.selectedInfoSimulation = selectedRunById.title;
          this.selectedCascadeSimulation = selectedRunById.title;
          return;
        }

        if (!this.runs.some((r) => r.title === this.selectedInfoSimulation)) {
          const baseline = this.runs.find((r) => (r.title || '').trim().toLowerCase() === 'basic simulation');
          const fallback = baseline ?? this.runs[0];
          this.selectedInfoSimulation = fallback?.title ?? 'Basic Simulation';
          this.selectedCascadeSimulation = this.selectedInfoSimulation;
        }

        const selectedRun = this.runs.find((r) => r.title === this.selectedInfoSimulation);
        if (selectedRun && selectedRun.id !== selId) {
          this.store.setSelectedRunId(selectedRun.id);
        }
      });
  }

  /** Safety net: if no city/baseline resolves shortly after entry, stop the endless skeleton. */
  private startLoadingWatchdog(): void {
    this.clearLoadingWatchdog();
    this.loadingWatchdog = setTimeout(() => {
      if (this.loadingBaseline && this.runs.length === 0) {
        console.warn('[T0] Baseline watchdog fired — releasing loader.');
        this.loadingBaseline = false;
      }
    }, 15000);
  }

  private stopLoading(): void {
    this.loadingBaseline = false;
    this.clearLoadingWatchdog();
  }

  private clearLoadingWatchdog(): void {
    if (this.loadingWatchdog) {
      clearTimeout(this.loadingWatchdog);
      this.loadingWatchdog = undefined;
    }
  }

  private periodToHorizon(period: PeriodLabel): Horizon {
    switch (period) {
      case '10 years':
        return 10;
      case '15 years':
        return 15;
      case '20 years':
        return 20;
      default:
        return 5;
    }
  }

  onSimulationChange(simTitle: string): void {
    this.selectedInfoSimulation = simTitle;
    this.selectedCascadeSimulation = simTitle;

    const run = this.runs.find((r) => r.title === simTitle);
    if (run) {
      this.store.setSelectedRunId(run.id);
    }
  }

  onPeriodChange(label: PeriodLabel): void {
    this.selectedInfoPeriod = label;
    this.horizon = this.periodToHorizon(label);
    this.yearStore.setHorizon(this.horizon);
  }

  trackByRunId = (_: number, r: AnalysisRun) => r.id;
}